import { useState } from 'react';
import type { UnifiedChatMessage, HostColumn } from '@/mocks/adminLiveChat';
import { hostColumnLabels, hostColumnIcons, getHostColumnMessages } from '@/mocks/adminLiveChat';
import UnifiedMessageCard from './UnifiedMessageCard';

interface Props {
  messages: UnifiedChatMessage[];
  selectedMessage: UnifiedChatMessage | null;
  onSelect: (msg: UnifiedChatMessage) => void;
  onToggleFavorite: (msg: UnifiedChatMessage) => void;
  onTogglePin: (msg: UnifiedChatMessage) => void;
  onMarkAir: (msg: UnifiedChatMessage) => void;
  onMarkPending: (msg: UnifiedChatMessage) => void;
  onBlock: (msg: UnifiedChatMessage) => void;
  onBanIP: (msg: UnifiedChatMessage) => void;
  onArchive: (msg: UnifiedChatMessage) => void;
}

const hostColumns: HostColumn[] = ['importantes', 'favoritos', 'saludos', 'canciones', 'pendientes'];

export default function HostSimpleView({
  messages,
  selectedMessage,
  onSelect,
  onToggleFavorite,
  onTogglePin,
  onMarkAir,
  onMarkPending,
  onBlock,
  onBanIP,
  onArchive,
}: Props) {
  const [activeColumn, setActiveColumn] = useState<HostColumn>('importantes');
  const [searchQuery, setSearchQuery] = useState('');

  const columnCounts = Object.fromEntries(
    hostColumns.map((col) => [col, getHostColumnMessages(messages, col).length])
  ) as Record<HostColumn, number>;

  const filteredMessages = getHostColumnMessages(messages, activeColumn).filter((m) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.senderName.toLowerCase().includes(q) ||
      m.content.toLowerCase().includes(q) ||
      (m.songRequest && m.songRequest.toLowerCase().includes(q)) ||
      (m.contestAnswer && m.contestAnswer.toLowerCase().includes(q))
    );
  });

  const totalNew = messages.filter((m) => m.status === 'nuevo' || m.status === 'pendiente').length;

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Top bar — simplified locutor view */}
      <div className="shrink-0 px-3 py-3 border-b border-background-200 bg-background-50">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-megaphone-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground-950">Cola del Locutor</h3>
              <p className="text-[9px] text-foreground-400">Modo lectura al aire</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {totalNew > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold">
                {totalNew} nuevos
              </span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-2.5">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar..."
            className="w-full pl-9 pr-8 py-2 rounded-xl bg-background-100 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors"
            >
              <i className="ri-close-line text-background-50 text-[8px]"></i>
            </button>
          )}
        </div>

        {/* Host column pills */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          {hostColumns.map((col) => {
            const isActive = activeColumn === col;
            const count = columnCounts[col] ?? 0;
            return (
              <button
                key={col}
                onClick={() => setActiveColumn(col)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                  isActive
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                <i className={`${hostColumnIcons[col]} ${isActive ? 'text-background-50' : ''} text-[11px]`}></i>
                {hostColumnLabels[col]}
                {count > 0 && (
                  <span className={`ml-0.5 px-1 py-0 text-[9px] rounded-full font-bold ${
                    isActive ? 'bg-background-50/30 text-background-50' : 'bg-background-200 text-foreground-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto">
        {filteredMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-inbox-line text-foreground-300 text-xl"></i>
            </div>
            <p className="text-sm font-bold text-foreground-400">Sin mensajes</p>
            <p className="text-[10px] text-foreground-400 mt-0.5">
              No hay mensajes en esta sección
            </p>
          </div>
        ) : (
          <>
            {filteredMessages.map((msg) => (
              <UnifiedMessageCard
                key={msg.id}
                message={msg}
                isSelected={selectedMessage?.id === msg.id}
                isHostView={true}
                onSelect={onSelect}
                onToggleFavorite={onToggleFavorite}
                onTogglePin={onTogglePin}
                onMarkAir={onMarkAir}
                onArchive={onArchive}
              />
            ))}
          </>
        )}
      </div>

      {/* Bottom action bar — big buttons for locutor */}
      {selectedMessage && (
        <div className="shrink-0 px-3 py-3 border-t border-background-200 bg-background-50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onMarkAir(selectedMessage)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-emerald-500 text-background-50 text-xs font-bold cursor-pointer hover:bg-emerald-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-broadcast-line text-sm"></i>
              Leer al aire
            </button>
            <button
              onClick={() => onToggleFavorite(selectedMessage)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-colors whitespace-nowrap ${
                selectedMessage.status === 'favorito'
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-amber-500 text-background-50 hover:bg-amber-600'
              }`}
            >
              <i className={`${selectedMessage.status === 'favorito' ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
              {selectedMessage.status === 'favorito' ? 'Quitar fav' : 'Favorito'}
            </button>
            <button
              onClick={() => onTogglePin(selectedMessage)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-colors whitespace-nowrap ${
                selectedMessage.status === 'fijado'
                  ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                  : 'bg-violet-500 text-background-50 hover:bg-violet-600'
              }`}
            >
              <i className={`${selectedMessage.status === 'fijado' ? 'ri-pushpin-fill' : 'ri-pushpin-line'} text-sm`}></i>
              {selectedMessage.status === 'fijado' ? 'Quitar' : 'Fijar'}
            </button>
            <button
              onClick={() => onMarkPending(selectedMessage)}
              className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl bg-orange-100 text-orange-700 text-xs font-bold cursor-pointer hover:bg-orange-200 transition-colors whitespace-nowrap"
              title="Pendiente"
            >
              <i className="ri-time-line text-sm"></i>
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => onBlock(selectedMessage)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[11px] font-semibold cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-forbid-2-line text-xs"></i>
              Bloquear
            </button>
            <button
              onClick={() => onBanIP(selectedMessage)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[11px] font-semibold cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-globe-line text-xs"></i>
              Banear IP
            </button>
            <button
              onClick={() => onArchive(selectedMessage)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-foreground-100 text-foreground-600 text-[11px] font-semibold cursor-pointer hover:bg-foreground-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-archive-line text-xs"></i>
              Archivar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}